import React, {
    useState,
    useEffect,
    useMemo,
    useCallback
} from 'react'
import {
    Dropdown,
    Label,
    Popup
} from 'semantic-ui-react'
import PropTypes from "prop-types";
import _ from 'lodash'
import './styles.css'
import { onError, onWarning } from './messages';

const DropDown = React.forwardRef((props, ref) => {
    const defaultSpec = useMemo(() => {
        return {}
    }, [])

    const {
        api,
        data,
        spec = defaultSpec,
        valueField,
        keyField,
        textField,
        imageField,
        descriptionField,
        labelField,
        value,
        onChange,
        onChangeItem,
        selectAll,
        filter,
        initial,
        selectFirst,
        onValueNotFound,
        itemClassName,
        sort,
        onSearchChange,
        onClose,
        selectedItems,
        ...rest
    } = props;

    const groupmultiple = props.groupmultiple !== undefined ? props.groupmultiple : true;

    const [options, setOptions] = useState()
    const [loading, setLoading] = useState(false)
    const [items, setItems] = useState()
    const [internalValue, setInternalValue] = useState(value)
    const [query, setQuery] = useState('');

    const hasValue = () => {
        return internalValue != null && internalValue !== undefined
    }

    useEffect(() => {
        setInternalValue(value)
    }, [value])

    useEffect(() => {
        let canceled = false
        const MIN_LOADING_DURATION = 300; // in ms
        if ((api && spec) || data) {
            setLoading(true);
            // Clear items too, otherwise an `initial`/`filter` change in the same commit rebuilds options from the stale list
            const previousItems = items;
            setItems(null);
            setOptions(null);
            const startTime = Date.now();
            (async () => {
                try {
                    let s = {}
                    if (spec) {
                        _.forOwn(spec, (value, key) => {
                            if (_.isArray(value)) {
                                _.forEach(value, (value, index) => {
                                    s[`${key}[${index}]`] = value
                                });
                            } else {
                                s[key] = value
                            }
                        })
                    }

                    let result = data ? data : await api(s);
                    if (hasValue() && onValueNotFound && _.isEmpty(result)) {
                        onValueNotFound();
                    }
                    if (!canceled) {
                        setItems(sort ? sort(result) : result)
                    }

                } catch (error) {
                    if (!canceled) setItems(previousItems)
                    error.status === 400 ? onWarning(error) : onError(error)
                } finally {
                    const elapsed = Date.now() - startTime;
                    const remaining = MIN_LOADING_DURATION - elapsed;
                    if (!canceled) {
                        if (!data && remaining > 0) {
                            setTimeout(() => {
                                if (!canceled) setLoading(false);
                            }, remaining);
                        } else {
                            setLoading(false);
                        }
                    }
                }
            })()
        } else {
            console.warn("Either api and spec or data are required")
        }
        return () => (canceled = true)
    }, [api, spec, data])

    useEffect(() => {
        if (items) {
            let result = [...items]
            if (initial && !_.find(result, r => r[valueField] === initial[valueField])) {
                result.push(initial)
            }
            
            // Merge selected items that are not in the current results (preserves selection during search)
            if (selectedItems && selectedItems.length > 0) {
                selectedItems.forEach(selectedItem => {
                    if (!_.find(result, r => r[valueField] === selectedItem[valueField])) {
                        result.push(selectedItem)
                    }
                })
            }

            let options = result.map(item => {
                let text = ""
                if (typeof (textField) === "function") {
                    text = textField(item)
                } else {
                    text = item[textField]
                }
                let description = null
                if (descriptionField) {
                    if (typeof (descriptionField) === "function") {
                        description = descriptionField(item)
                    } else {
                        description = item[descriptionField]
                    }
                }
                let label = null
                if (labelField) {
                    if (typeof (labelField) === "function") {
                        label = labelField(item)
                    } else {
                        label = {
                            horisontal: "true",
                            basic: true,
                            content: item[labelField]
                        }
                    }
                }

                let className = null
                if (itemClassName) {
                    if (typeof (itemClassName) === "function") {
                        className = itemClassName(item)
                    } else {
                        className = itemClassName
                    }
                }

                let image = null;
                if (imageField) {
                    if (typeof (imageField) === "function") {
                        image = imageField(item)
                    } else {
                        image = item[imageField]
                    }
                }

                return {
                    value: item[valueField],
                    text: text,
                    item: item,
                    key: item[keyField || valueField],
                    disabled: item.disabled,
                    description: description,
                    className: className,
                    label: label ? label : null,
                    image: image ? {
                        width: 24,
                        height: 24,
                        wrapped: true,
                        src: image
                    } : null
                }
            })
            options = filter ? filter(options, value) : options

            // Check if current value exists in options and notify parent with updated item data
            if (hasValue() && options && onChangeItem) {
                if (Array.isArray(internalValue)) {
                    const selected = options.filter(o => internalValue.includes(o.value));
                    onChangeItem(selected.map(o => o.item));
                } else {
                    let valueExists = options.find(o => o.value === internalValue)
                    if (valueExists) {
                        onChangeItem(valueExists.item)
                    }
                }
            }

            if (!hasValue() && selectFirst) {
                let firstResult = _.first(options)
                if (typeof (selectFirst) === "function") {
                    firstResult = selectFirst(options)
                }

                if (firstResult) {
                    onChange?.(firstResult.value)
                    onChangeItem?.(firstResult.item)
                }
            }

            if (!hasValue() && rest.multiple && selectAll) {
                const val = options.map(x => x.value)
                onChange?.(val.value)
                onChangeItem?.(val.map(x => x.item))
            }
            setOptions(options)
        }
    }, [items, filter, initial, selectedItems])


    useEffect(() => {
        // Skip if options array is not yet initialized or if we're still loading new data
        if (options && !loading) {
            // Check to see if value is still valid after new options are set
            if (Array.isArray(internalValue)) {
                const selected = options.filter((o) => internalValue.includes(o.value));
                // if the selected length is not equal to the value length, then the value is not valid
                if (selected.length !== internalValue.length) {
                    onChange?.(selected?.map(o => o.value))
                    onChangeItem?.(selected?.map(o => o.item));
                    setInternalValue(selected?.map(o => o.value))
                }
            } else if (internalValue !== null && internalValue !== undefined) {
                const selected = options.find(o => o.value === internalValue)
                if (!selected) {
                    onChange?.(null)
                    onChangeItem?.(null)
                    setInternalValue(null)
                }
            }
        }
    }, [options, internalValue, loading])

    const onValueChange = useCallback((e, { value }) => {
        // Handle special case of clear value
        if (value === "" || value === null) {
            onChange?.(value)
            onChangeItem?.(null);
        }
        else {
            if (Array.isArray(value)) {
                const selected = (options || []).filter((o) => value.includes(o.value));
                onChange?.(selected?.map(o => o.value))
                onChangeItem?.(selected?.map(o => o.item));
                setInternalValue(selected?.map(o => o.value))
            } else {
                const selected = (options || []).find(o => o.value === value)
                if (selected === undefined || selected === null) {
                    onChange?.(null)
                    onChangeItem?.(null)
                    setInternalValue(null)
                } else {
                    onChange?.(value)
                    onChangeItem?.(selected.item);
                    setInternalValue(value)
                }
            }
        }
    }, [onChange, onChangeItem, options])

    const style = useMemo(() => {
        return {
            minWidth: 0
        }
    }, [])

    const renderLabel = (item, index, defaultLabelProps) => {3
        if (index > 0 && groupmultiple) {
            if (index == 1) {
                return <Popup 
                className='dropdown-labels-popup'
                hoverable
                wide
                trigger={
                    <Label>+{internalValue.length - 1}</Label>
                }>
                    <Popup.Content style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {
                            internalValue.slice(1).map((value) => {
                                const item = (options || []).find(o => o.value === value)
                                const props = {
                                    ...defaultLabelProps,
                                    key: value,
                                    value: value,
                                    content: item?.text
                                }
                                return <Label {...props} key={value} />
                            })
                        }
                    </Popup.Content>
                </Popup>
            }
        }
        else {
            return {
                content: item.text,
                ...defaultLabelProps
            }
        }
    }
        
    return <Dropdown ref={
        ref
    }
    
        selection
        loading={loading}
        options={
            options || []
        }
        value={
            hasValue() == false && rest.multiple ? [] : internalValue
        }
        onChange={onValueChange}
        renderLabel = {renderLabel}
        style={style}
        search={true}
        searchQuery={query}
        onSearchChange={(e, { searchQuery }) => {
            setQuery(searchQuery);
            onSearchChange?.(searchQuery);
        }}
        onClose={(e, data)=>{ setQuery(""); onClose?.(e, data); }}
        {...rest}
        compact
    />
})
DropDown.displayName = "DropDown"
DropDown.propTypes = {
    api: PropTypes.func,
    spec: PropTypes.object,
    valueField: PropTypes.string.isRequired,
    keyField: PropTypes.string,
    textField: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
    imageField: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    descriptionField: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    labelField: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    value: PropTypes.any,
    onChange: PropTypes.func,
    data: PropTypes.any,
    onChangeItem: PropTypes.func,
    filter: PropTypes.func,
    initial: PropTypes.any,
    selectFirst: PropTypes.any,
    onValueNotFound: PropTypes.func,
    itemClassName: PropTypes.any,
    selectAll: PropTypes.bool,
    sort: PropTypes.func,
    groupmultiple: PropTypes.bool,
    onSearchChange: PropTypes.func,
    onClose: PropTypes.func,
    selectedItems: PropTypes.array
}
export default DropDown
